/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { ReactNode } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import highComplexity from './images/high-complexity.png';
import lowComplexity from './images/low-complexity.png';
import mediumComplexity from './images/medium-complexity.png';

const styles = cssMap({
	root: {
		display: 'grid',
		gridTemplateColumns: '1fr',
		gap: token('space.200'),
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		paddingBlockStart: token('space.200'),
		paddingInlineEnd: token('space.200'),
		paddingBlockEnd: token('space.200'),
		paddingInlineStart: token('space.200'),
		borderRadius: token('radius.large'),
		'@media (min-width: 48rem)': {
			gridTemplateColumns: '1fr 1fr',
		},
	},
	content: {
		width: '100%',
		height: '100%',
	},
	image: {
		width: '100%',
		maxWidth: 'fit-content',
	},
});

interface UICardProps {
	children: ReactNode;
}

export const UICard: ({ children }: UICardProps) => JSX.Element = ({ children }: UICardProps) => {
	return <Box xcss={styles.root}>{children}</Box>;
};

const data = [
	{
		heading: 'Low complexity',
		subheading: 'Low cognitive load',
		body: 'Only use text when features are straightforward and easy to understand through the UI.',
		src: lowComplexity,
		alt: 'Example of a low complexity, text-only spotlight that is describing how to create a page.',
	},
	{
		heading: 'Medium complexity',
		subheading: 'Moderate cognitive load',
		body: 'Use a single-frame illustration with a clear focal point for features that offer straightforward and intuitive interactions, but require a visual reference for comprehension. ',
		src: mediumComplexity,
		alt: 'Example of a medium complexity spotlight that includes a single frame illustration that is describing creating tasks with the right information.',
	},
	{
		heading: 'High complexity',
		subheading: 'High cognitive load',
		body: 'If a feature is complex, use multiple images or a short video (like a GIF or MP4) to help explain how it works.',
		src: highComplexity,
		alt: 'Example of a high complexity spotlight that includes a short video that is describing the benefit of creating a huddle for your team.',
	},
];

// TODO: Fill in the component {description} and ensure links point to the correct {packageName} location.
// Remove links that the component does not have (such as usage). If there are no links remove them all.
/**
 * __Spotlight visual guidelines__
 *
 * A spotlight visual guidelines {description}.
 *
 * - [Examples](https://atlassian.design/components/{packageName}/examples)
 * - [Code](https://atlassian.design/components/{packageName}/code)
 * - [Usage](https://atlassian.design/components/{packageName}/usage)
 */
export const SpotlightVisualGuidelines: () => JSX.Element = () => {
	return (
		<Stack space="space.100">
			{data.map(({ heading, subheading, body, src, alt }) => (
				<UICard>
					<Stack alignBlock="center" space="space.200" xcss={styles.content}>
						<Stack space="space.100">
							<Heading size="medium">{heading}</Heading>
							<Text>{subheading}</Text>
						</Stack>
						<Text>{body}</Text>
					</Stack>
					<img css={styles.image} src={src} alt={alt} />
				</UICard>
			))}
		</Stack>
	);
};
